import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import {addToast, Button, Heading, Pagination, Text} from '@albert-io/atomic';
import ClassroomInviteBanner from 'client/components/ClassroomInviteBanner/ClassroomInviteBanner.react';
import {pushQueryParams} from 'client/history';

import JoinCodeForm from '../JoinCodeForm/JoinCodeForm.react';
import {useStudentDashboard} from '../StudentDashboard.context';

import emptyClassroomMenuImg from './emptyClassroomMenu.svg';
import ClassroomMenuCard from './ClassroomMenuCard.react';

import './classroom-menu.scss';

const ClassroomMenu = ({className}) => {
  const {
    classrooms,
    duplicateClassroom,
    enrollStudent,
    invitations,
    isReady,
    isRequestPending,
    page,
    totalPages
  } = useStudentDashboard();

  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (duplicateClassroom) {
      setShowModal(false);
    }
  }, [duplicateClassroom]);

  const memoizedClassrooms = useMemo(
    () =>
      classrooms.map((classroom) => {
        const id = classroom.getId();
        return (
          <li className='classrooms_viewer__list__item' key={id}>
            <ClassroomMenuCard classroom={classroom} />
          </li>
        );
      }),
    [classrooms]
  );

  if (!isReady) {
    return null;
  }

  const hasNeitherClassroomsNorInvitations = classrooms.isEmpty() && invitations.isEmpty();
  if (hasNeitherClassroomsNorInvitations) {
    return (
      <section
        className={classnames(
          'u-display_flex u-flex-wrap_wrap classroom-menu classroom-menu--empty',
          className
        )}
      >
        <img
          alt=''
          className='classroom-menu__hero-img u-mar-lr_auto'
          src={emptyClassroomMenuImg}
        />
        <div className='u-display_flex u-justify-content_center u-flex-direction_column u-mar-tb_4 u-width_100pc'>
          <Heading as='h2' className='u-text-align_center' size='s'>
            You have no classes yet!
          </Heading>
          <Text as='p' className='u-text-align_center'>
            If you have a class enrollment code, enter the code below.
          </Text>
          <JoinCodeForm className='u-display_flex u-flex-direction_column'>
            <JoinCodeForm.Input placeholder='Enter code' />
            <JoinCodeForm.SubmitBtn className='u-width_100pc' />
          </JoinCodeForm>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className={classnames('u-display_flex u-flex-wrap_wrap classroom-menu', className)}>
        <div className='u-display_flex u-flex-wrap_wrap u-align-items_center u-justify-content_space-between u-mar-b_4 u-width_100pc'>
          <Heading as='h2' className='u-mar-b_0' size='l'>
            Classes
          </Heading>
          <Button
            className='classroom-menu__modal-btn'
            onClick={() => {
              setShowModal(true);
            }}
          >
            <span className='classroom-menu__modal-btn__text'>Join a class</span>
          </Button>
        </div>
        <ul className='classroom-menu__invite-list u-list-style_none u-pad_0 u-mar_0 u-width_100pc'>
          {invitations.map((invitation) => (
            <ClassroomInviteBanner
              as='li'
              disabled={isRequestPending}
              invitation={invitation}
              key={invitation.getId()}
              onClick={() => {
                enrollStudent({
                  relationship: {
                    id: invitation.getClassroom().getId(),
                    type: 'classroom_v1'
                  },
                  onReject: () => {
                    addToast({
                      title: 'Something went wrong',
                      type: 'danger',
                      message:
                        'We were unable to complete your enrollment. Please verify your invitation with your teacher and try again.'
                    });
                  }
                });
              }}
            />
          ))}
        </ul>
        <ul className='u-list-style_none classroom-menu__list'>{memoizedClassrooms}</ul>
        {totalPages > 1 ? (
          <div className='u-text-align_center u-display_block u-width_100pc u-mar-t_5'>
            <Pagination
              defaultPage={page}
              onChange={(nextPage) => pushQueryParams({page: nextPage})}
              totalPages={totalPages}
            />
          </div>
        ) : null}
      </section>
      {showModal ? (
        <JoinCodeForm.Modal
          handleClose={() => {
            setShowModal(false);
          }}
          onSubmit={() => {
            setShowModal(false);
          }}
        />
      ) : null}
    </>
  );
};

ClassroomMenu.propTypes = {
  className: PropTypes.string
};

export default ClassroomMenu;
